import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  Camera, 
  UserPlus, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Settings, 
  Download,
  Loader2,
  RefreshCw,
  Users,
  FileText,
  Send,
  Building2,
  Shield,
  Wifi,
  WifiOff,
  LogIn,
  LogOut,
  User,
  Trash2,
  Eye,
  EyeOff,
  ScanFace,
  Home,
  AlertCircle,
  ChevronLeft,
  Key,
  Database,
  CloudOff,
  Cloud,
  Filter,
  Zap,
  CheckCircle,
  Circle,
  Smartphone,
  Monitor,
  RotateCcw
} from 'lucide-react';
import {
  initDB,
  getBiometriasOffline,
  contarBiometriasOffline,
  salvarPontoOffline,
  getPontosPendentes,
  getConfigDispositivo,
  atualizarConfigDispositivo,
  sincronizarBiometrias,
  enviarPontosPendentes,
  sincronizacaoCompleta,
  reconhecerFaceOffline,
  temBiometriasOffline,
  getStatusOffline
} from './offlineStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Versão do app - atualizar a cada deploy para forçar refresh
const APP_VERSION = '2.2.0';
const VERSION_KEY = 'jornyx_empresa_version';

// Função para verificar e forçar atualização
const checkForUpdates = () => {
  const savedVersion = localStorage.getItem(VERSION_KEY);
  if (savedVersion !== APP_VERSION) {
    console.log(`Nova versão detectada: ${savedVersion} -> ${APP_VERSION}`);
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    
    // Limpar caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Forçar reload se não for primeira vez
    if (savedVersion) {
      toast.info('Nova versão disponível! Atualizando...');
      setTimeout(() => {
        window.location.reload(true);
      }, 1500);
      return true;
    }
  }
  return false;
};

// Hook para PWA Install
const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Verificar se já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      toast.success('App instalado com sucesso!');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) return false;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setIsInstallable(false);
      return true;
    }
    return false;
  };

  return { isInstallable, isInstalled, installApp };
};

// ==================== TELA DE SELEÇÃO DA EMPRESA ====================
const TelaSelecaoEmpresa = ({ onEmpresaSelecionada }) => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empresaSelecionada, setEmpresaSelecionada] = useState('');

  useEffect(() => {
    fetchEmpresas();
  }, []);

  const fetchEmpresas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/empresas-ponto/listar`);
      if (res.ok) {
        const data = await res.json();
        setEmpresas(data.empresas || []);
      }
    } catch (err) {
      console.error('Erro ao buscar empresas:', err);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleContinuar = () => {
    if (!empresaSelecionada) {
      toast.error('Selecione uma empresa');
      return;
    }
    const empresa = empresas.find(e => e.id === empresaSelecionada);
    if (empresa) {
      const config = {
        empresaId: empresa.id,
        empresaNome: empresa.razao_social,
        modoRep: 'REP-P',
        dispositivoId: `TOTEM-${Date.now()}`
      };
      localStorage.setItem('jornyx_totem_empresa', JSON.stringify(config));
      onEmpresaSelecionada(config);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-slate-800/90 border-slate-700 shadow-2xl">
        <CardHeader className="text-center border-b border-slate-700 pb-6">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
            <Clock className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">JORNYX Ponto</CardTitle>
          <CardDescription className="text-slate-400">
            Selecione a empresa para iniciar
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Empresa
                </label>
                <select
                  value={empresaSelecionada}
                  onChange={(e) => setEmpresaSelecionada(e.target.value)}
                  className="w-full h-12 bg-slate-700 border-slate-600 text-white rounded-lg px-4"
                  data-testid="select-empresa"
                >
                  <option value="">Selecione...</option>
                  {empresas.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.razao_social}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                onClick={handleContinuar}
                disabled={!empresaSelecionada}
                className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-lg font-semibold"
                data-testid="btn-iniciar"
              >
                <ScanFace className="w-6 h-6 mr-2" />
                Iniciar Ponto
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== MENU PRINCIPAL DO TOTEM ====================
const MenuPrincipal = ({ config, onNavigate, statusOffline }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [online, setOnline] = useState(navigator.onLine);
  const [updating, setUpdating] = useState(false);
  const { isInstallable, isInstalled, installApp } = usePWAInstall();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const handleForceUpdate = async () => {
    setUpdating(true);
    try {
      // Limpar todos os caches
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
      }
      
      // Limpar Service Worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
      }
      
      // Limpar IndexedDB dos modelos (face-api usa isso internamente)
      if ('indexedDB' in window) {
        indexedDB.deleteDatabase('tensorflowjs');
      }
      
      toast.success('Cache limpo! Recarregando...');
      
      // Forçar reload completo com cache-busting
      setTimeout(() => {
        window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
      }, 1000);
    } catch (err) {
      console.error('Erro ao limpar cache:', err);
      toast.error('Erro ao atualizar. Tente limpar manualmente (Ctrl+Shift+R)');
      setUpdating(false);
    }
  };

  const handleInstallApp = async () => {
    const installed = await installApp();
    if (!installed) {
      toast.info('Para instalar manualmente, use o menu do navegador e selecione "Instalar aplicativo"');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header Centralizado */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-3">
          <Clock className="w-8 h-8 text-white" />
        </div>
        <p className="text-lg text-slate-300">{config.empresaNome}</p>
        <div className="text-4xl font-bold text-white font-mono mt-2">
          {formatTime(currentTime)}
        </div>
      </div>

      {/* Menu Grid - 4 botões compactos */}
      <div className="max-w-sm mx-auto grid grid-cols-2 gap-4">
        {/* Bater Ponto */}
        <Card 
          className="bg-gradient-to-br from-emerald-600 to-teal-700 border-0 cursor-pointer transform transition-all hover:scale-105 hover:shadow-xl"
          onClick={() => onNavigate('ponto')}
          data-testid="menu-bater-ponto"
        >
          <CardContent className="p-5 text-center">
            <div className="w-12 h-12 mx-auto bg-white/20 rounded-xl flex items-center justify-center mb-2">
              <ScanFace className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-sm font-bold text-white">Bater Ponto</h3>
          </CardContent>
        </Card>

        {/* Cadastrar Empregados */}
        <Card 
          className="bg-gradient-to-br from-amber-600 to-orange-700 border-0 cursor-pointer transform transition-all hover:scale-105 hover:shadow-xl"
          onClick={() => onNavigate('login-admin')}
          data-testid="menu-cadastrar"
        >
          <CardContent className="p-5 text-center">
            <div className="w-12 h-12 mx-auto bg-white/20 rounded-xl flex items-center justify-center mb-2">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-sm font-bold text-white">Cadastrar Empregados</h3>
          </CardContent>
        </Card>

        {/* Exportar AFD */}
        <Card 
          className="bg-gradient-to-br from-blue-600 to-indigo-700 border-0 cursor-pointer transform transition-all hover:scale-105 hover:shadow-xl"
          onClick={() => onNavigate('exportar')}
          data-testid="menu-exportar"
        >
          <CardContent className="p-5 text-center">
            <div className="w-12 h-12 mx-auto bg-white/20 rounded-xl flex items-center justify-center mb-2">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-sm font-bold text-white">Exportar AFD</h3>
          </CardContent>
        </Card>

        {/* Configurações */}
        <Card 
          className="bg-gradient-to-br from-purple-600 to-violet-700 border-0 cursor-pointer transform transition-all hover:scale-105 hover:shadow-xl"
          onClick={() => onNavigate('config-offline')}
          data-testid="menu-config"
        >
          <CardContent className="p-5 text-center">
            <div className="w-12 h-12 mx-auto bg-white/20 rounded-xl flex items-center justify-center mb-2">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-sm font-bold text-white">Configurações</h3>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="text-center mt-8 text-slate-500 text-xs">
        Jornyx v{APP_VERSION}
      </div>
    </div>
  );
};

// ==================== TELA DE PONTO OTIMIZADA ====================
const TelaPonto = ({ config, onVoltar }) => {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelError, setModelError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [lastPunch, setLastPunch] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [faceDetected, setFaceDetected] = useState(false);
  const [detectionMessage, setDetectionMessage] = useState('Posicione seu rosto na câmera');
  const [statusOffline, setStatusOffline] = useState(null);
  const [modoOffline, setModoOffline] = useState(false);
  const [pontoLogs, setPontoLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [isMobile, setIsMobile] = useState(null); // null = não selecionado ainda
  const [showDeviceSelector, setShowDeviceSelector] = useState(true); // Mostrar seleção de dispositivo
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  const lastDetectionRef = useRef(null);
  
  // Função para adicionar logs visíveis
  const addLog = (msg, type = 'info') => {
    const now = new Date();
    const time = now.toLocaleTimeString('pt-BR');
    setPontoLogs(prev => [...prev.slice(-30), { time, msg, type }]);
    console.log(`[TelaPonto ${type}] ${msg}`);
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      if (statusOffline?.pontos_pendentes > 0) {
        toast.info('Conexão restaurada. Sincronizando pontos pendentes...');
      }
    };
    const handleOffline = () => {
      setOnline(false);
      toast.warning('Sem conexão. Modo offline ativado.');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [statusOffline]);

  // Só inicializa o sistema após selecionar o dispositivo
  useEffect(() => {
    if (isMobile !== null && !showDeviceSelector) {
      initializeSystem();
    }
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
    };
  }, [isMobile, showDeviceSelector]);

  // Função para selecionar dispositivo
  const handleSelectDevice = (mobile) => {
    setIsMobile(mobile);
    setShowDeviceSelector(false);
    addLog(`Dispositivo selecionado: ${mobile ? 'Celular' : 'Desktop/Tablet'}`, 'info');
  };

  const initializeSystem = async () => {
    addLog('Inicializando sistema...', 'info');
    
    // Inicializar IndexedDB
    await initDB();
    addLog('IndexedDB inicializado', 'success');
    
    // Verificar status offline
    const status = await getStatusOffline(config.empresaId);
    setStatusOffline(status);
    addLog(`Status offline: ${status?.biometrias_offline || 0} biometrias locais`, 'info');
    
    // Verificar se tem biometrias offline
    const temOffline = await temBiometriasOffline(config.empresaId);
    setModoOffline(!navigator.onLine || temOffline);
    addLog(`Modo: ${!navigator.onLine || temOffline ? 'OFFLINE' : 'ONLINE'}`, 'info');
    
    // Carregar modelos
    await loadModels();
  };

  const loadModels = async () => {
    try {
      setModelError(null);
      addLog('Carregando modelos de IA...', 'info');
      
      // Verificar backend do TensorFlow.js
      const tf = faceapi.tf;
      const backend = tf.getBackend();
      addLog(`TensorFlow.js backend: ${backend || 'não inicializado'}`, 'info');
      
      // Tentar forçar WebGL se disponível
      if (!backend || backend === 'cpu') {
        try {
          await tf.setBackend('webgl');
          await tf.ready();
          addLog(`Backend alterado para: ${tf.getBackend()}`, 'success');
        } catch (backendErr) {
          addLog(`Não foi possível usar WebGL: ${backendErr.message}`, 'warning');
        }
      }
      
      // URL do CDN externo (jsDelivr) - mais confiável que servidor local
      const CDN_MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/model';
      const LOCAL_MODEL_URL = `${window.location.origin}/models`;
      
      // Tentar primeiro o CDN, fallback para local
      let MODEL_URL = CDN_MODEL_URL;
      
      try {
        const cdnCheck = await fetch(`${CDN_MODEL_URL}/tiny_face_detector_model-weights_manifest.json`, { 
          method: 'HEAD',
          mode: 'cors'
        });
        if (!cdnCheck.ok) {
          addLog('CDN não acessível, usando local', 'info');
          MODEL_URL = LOCAL_MODEL_URL;
        } else {
          addLog('Usando CDN jsDelivr', 'success');
        }
      } catch (cdnErr) {
        addLog(`CDN falhou: ${cdnErr.message}`, 'error');
        MODEL_URL = LOCAL_MODEL_URL;
      }
      
      addLog(`URL dos modelos: ${MODEL_URL.substring(0, 50)}...`, 'info');
      
      // OTIMIZAÇÃO: Carregar modelos TINY para máxima velocidade
      // TinyFaceDetector: ~200KB (vs 6MB do SSD)
      // faceLandmark68TinyNet: ~80KB (vs 350KB do faceLandmark68Net)
      addLog('Carregando modelos OTIMIZADOS (Tiny)...', 'info');
      
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),  // TINY version
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
      ]);
      
      addLog('Modelos Tiny carregados ✓', 'success');
      
      // SSD MobileNet como fallback (carrega em background se necessário)
      faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL).then(() => {
        addLog('SSD MobileNet (fallback) carregado em background', 'info');
      }).catch(() => {});
      
      setModelsLoaded(true);
      addLog(`Todos os modelos carregados! Backend: ${tf.getBackend()}`, 'success');
      toast.success('Sistema pronto para uso');
    } catch (err) {
      console.error('TelaPonto: Erro ao carregar modelos:', err);
      addLog(`Erro ao carregar modelos: ${err.message}`, 'error');
      
      let errorMessage = err.message;
      if (err.message.includes('tensor') || err.message.includes('shape')) {
        errorMessage = `Erro de modelo corrompido: ${err.message}. Use o botão "Forçar Atualização" no menu principal para resolver.`;
      }
      
      setModelError(errorMessage);
    }
  };

  useEffect(() => {
    if (modelsLoaded && videoRef.current) {
      addLog('Modelos carregados, iniciando câmera...', 'info');
      startCamera();
    }
  }, [modelsLoaded]);

  // Fallback: se após 5s ainda não tiver câmera, tentar iniciar
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (modelsLoaded && !cameraReady) {
        addLog('Fallback: tentando iniciar câmera após timeout', 'warning');
        startCamera(facingMode);
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, [modelsLoaded, cameraReady]);

  const trocarCamera = async () => {
    addLog('Trocando câmera...', 'info');
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    
    // Parar stream atual
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }
    setCameraReady(false);
    
    // Reiniciar com nova câmera
    setTimeout(() => startCamera(newMode), 500);
  };

  const startCamera = async (mode = facingMode) => {
    addLog(`Iniciando câmera ${mode === 'user' ? 'frontal' : 'traseira'}...`, 'info');
    try {
      // Parar câmera anterior se existir
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Detectar se é mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      addLog(`Dispositivo: ${isMobile ? 'Mobile' : 'Desktop'}`, 'info');
      
      // PADRÃO BIOMÉTRICO: Proporção 4:3 (640x480) ou 3:4 para retrato
      // Mobile em retrato: solicitar 480x640 (3:4)
      // Desktop/Tablet: 640x480 (4:3)
      const constraints = {
        video: {
          facingMode: mode,
          width: { ideal: isMobile ? 480 : 640 },
          height: { ideal: isMobile ? 640 : 480 },
          aspectRatio: { ideal: isMobile ? 0.75 : 1.333 } // 3:4 ou 4:3
        },
        audio: false
      };
      
      addLog(`Constraints: ${isMobile ? '480x640 (3:4)' : '640x480 (4:3)'}`, 'info');
      
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (constraintErr) {
        addLog(`Constraints específicas falharam: ${constraintErr.message}`, 'warning');
        addLog('Tentando com constraints básicas...', 'info');
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: mode }, 
          audio: false 
        });
      }
      
      addLog(`Stream obtido: ${stream.getVideoTracks().length} tracks`, 'success');
      
      // Log das configurações reais do stream
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        addLog(`Resolução obtida: ${settings.width}x${settings.height}`, 'success');
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        addLog('srcObject atribuído ao video element', 'info');
        
        // Handlers de evento do vídeo
        const handleVideoReady = () => {
          const v = videoRef.current;
          if (v && v.videoWidth > 0) {
            addLog(`Vídeo renderizando: ${v.videoWidth}x${v.videoHeight}`, 'success');
            setCameraReady(true);
            startDetectionLoop();
          }
        };
        
        videoRef.current.onloadeddata = handleVideoReady;
        videoRef.current.oncanplay = handleVideoReady;
        
        // Forçar play()
        try {
          await videoRef.current.play();
          addLog('play() executado', 'success');
        } catch (playErr) {
          addLog(`play() erro: ${playErr.message}`, 'warning');
        }
        
        // Fallback: verificar periodicamente
        let attempts = 0;
        const checkInterval = setInterval(() => {
          attempts++;
          const v = videoRef.current;
          if (v && v.videoWidth > 0 && !v.paused) {
            addLog(`Vídeo OK (${v.videoWidth}x${v.videoHeight})`, 'success');
            clearInterval(checkInterval);
            if (!cameraReady) {
              setCameraReady(true);
              startDetectionLoop();
            }
          }
          if (attempts >= 10) {
            clearInterval(checkInterval);
            addLog('Timeout aguardando vídeo', 'error');
          }
        }, 500);
      }
    } catch (err) {
      addLog(`Erro câmera: ${err.name} - ${err.message}`, 'error');
      toast.error('Não foi possível acessar a câmera');
    }
  };

  const startDetectionLoop = () => {
    addLog('Iniciando detecção facial otimizada (TinyFaceDetector)...', 'info');
    
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    // Opções ULTRA otimizadas para TinyFaceDetector
    // inputSize: 160 é o menor possível, mais rápido
    const tinyOptions = new faceapi.TinyFaceDetectorOptions({ 
      inputSize: 160,      // Mínimo para máxima velocidade
      scoreThreshold: 0.3  // Mais tolerante para feedback rápido
    });

    detectionIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || processing) return;
      
      // Verificar se o vídeo está pronto
      if (videoRef.current.readyState < 2) {
        return; // Vídeo ainda não está pronto
      }

      try {
        // Detecção ULTRA RÁPIDA com TinyFaceDetector (inputSize: 160)
        const detection = await faceapi
          .detectSingleFace(videoRef.current, tinyOptions);

        if (detection) {
          setFaceDetected(true);
          setDetectionMessage('Rosto detectado - Toque na tela para registrar');
          lastDetectionRef.current = Date.now();
        } else {
          setFaceDetected(false);
          setDetectionMessage('Posicione seu rosto na câmera');
          lastDetectionRef.current = null;
        }
      } catch (err) {
        console.error('TelaPonto: Erro na detecção:', err);
      }
    }, 100); // Check a cada 100ms para feedback ultra fluido
  };

  // Função chamada quando usuário toca/clica na tela para registrar ponto
  const handleRegistrarPonto = async () => {
    if (processing || !faceDetected) {
      if (!faceDetected) {
        toast.error('Posicione seu rosto na câmera primeiro');
      }
      return;
    }
    await processarReconhecimento();
  };

  const processarReconhecimento = async () => {
    if (processing || !videoRef.current) return;
    
    setProcessing(true);
    setDetectionMessage('Processando reconhecimento...');
    lastDetectionRef.current = null;
    
    const startTime = performance.now();
    addLog('Iniciando processamento de reconhecimento...', 'info');

    try {
      // OTIMIZAÇÃO MÁXIMA: Usar APENAS modelos Tiny para velocidade
      // TinyFaceDetector + faceLandmark68TinyNet + faceRecognitionNet
      const tinyOptions = new faceapi.TinyFaceDetectorOptions({ 
        inputSize: 160,      // Menor = mais rápido (160 é o mínimo)
        scoreThreshold: 0.4  // Mais tolerante
      });
      
      // Detecção + Landmarks + Embedding em uma única chamada otimizada
      const detection = await faceapi
        .detectSingleFace(videoRef.current, tinyOptions)
        .withFaceLandmarks(true)  // true = usar faceLandmark68TinyNet
        .withFaceDescriptor();
      
      const detectTime = performance.now();
      
      if (!detection) {
        addLog(`Nenhum rosto detectado (${(detectTime - startTime).toFixed(0)}ms)`, 'error');
        toast.error('Nenhum rosto detectado');
        setProcessing(false);
        return;
      }

      const embeddingDetectado = Array.from(detection.descriptor);
      addLog(`Embedding extraído em ${(detectTime - startTime).toFixed(0)}ms (${embeddingDetectado.length} dimensões)`, 'success');
      
      await processarEmbedding(embeddingDetectado, startTime);
      
    } catch (err) {
      console.error('Erro no reconhecimento:', err);
      addLog(`Erro no reconhecimento: ${err.message}`, 'error');
      toast.error('Erro ao processar reconhecimento');
      setProcessing(false);
      setDetectionMessage('Posicione seu rosto na câmera');
    }
  };
  
  const processarEmbedding = async (embeddingDetectado, startTime) => {
    try {
      let resultado;

      // Tentar reconhecimento
      if (online) {
        // Modo online - usar API
        addLog('Enviando para API (modo online)...', 'info');
        resultado = await reconhecerOnline(embeddingDetectado);
      } else {
        // Modo offline - usar IndexedDB
        addLog('Buscando no IndexedDB (modo offline)...', 'info');
        resultado = await reconhecerFaceOffline(config.empresaId, embeddingDetectado, 0.6);
      }

      const totalTime = performance.now() - startTime;
      
      if (resultado && resultado.encontrado) {
        addLog(`✓ Funcionário reconhecido: ${resultado.empregado?.nome || 'N/A'} (distância: ${resultado.distancia?.toFixed(4) || 'N/A'}) - Total: ${totalTime.toFixed(0)}ms`, 'success');
        await registrarPonto(resultado.empregado, embeddingDetectado, resultado.distancia);
      } else {
        addLog(`✗ Funcionário NÃO reconhecido - Total: ${totalTime.toFixed(0)}ms`, 'error');
        toast.error('Funcionário não reconhecido. Verifique se sua biometria está cadastrada.');
      }
    } catch (err) {
      console.error('Erro no reconhecimento:', err);
      addLog(`Erro no reconhecimento: ${err.message}`, 'error');
      toast.error('Erro ao processar reconhecimento');
    } finally {
      setProcessing(false);
      setDetectionMessage('Posicione seu rosto na câmera');
    }
  };

  const reconhecerOnline = async (embedding) => {
    try {
      const res = await fetch(`${API_URL}/api/empresas-ponto/registrar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          empresa_id: config.empresaId,
          embedding: embedding,
          modo_rep: config.modoRep,
          dispositivo_id: config.dispositivoId
        })
      });

      const data = await res.json();
      addLog(`API respondeu: ${res.status} - reconhecido: ${data.reconhecido}`, data.reconhecido ? 'success' : 'error');
      
      if (data.reconhecido) {
        return {
          encontrado: true,
          empregado: {
            empregado_id: data.empregado_id,
            nome: data.empregado_nome
          },
          distancia: data.distancia,
          ponto_ja_registrado: true, // API já registrou
          comprovante: data.comprovante
        };
      }
      
      return { encontrado: false };
    } catch (err) {
      console.error('Erro na API:', err);
      addLog(`Erro na API: ${err.message} - Tentando offline...`, 'error');
      // Fallback para offline se API falhar
      return await reconhecerFaceOffline(config.empresaId, embedding, 0.6);
    }
  };

  const registrarPonto = async (empregado, embedding, distancia) => {
    const agora = new Date();
    
    // Se já foi registrado pela API online, apenas mostrar comprovante
    if (empregado.ponto_ja_registrado) {
      setLastPunch({
        nome: empregado.nome,
        hora: agora.toLocaleTimeString('pt-BR'),
        tipo: empregado.comprovante?.tipo || 'ponto'
      });
      addLog(`Ponto registrado: ${empregado.nome} às ${agora.toLocaleTimeString('pt-BR')}`, 'success');
      toast.success(`Ponto registrado para ${empregado.nome}!`);
      return;
    }

    // Registro offline
    const pontoData = {
      empresa_id: config.empresaId,
      empregado_id: empregado.empregado_id,
      empregado_nome: empregado.nome,
      timestamp: agora.toISOString(),
      tipo: 'ponto',
      embedding_usado: embedding,
      distancia: distancia,
      dispositivo_id: config.dispositivoId
    };

    if (online) {
      // Tentar registrar online
      try {
        // API já registrou no reconhecerOnline
      } catch (err) {
        // Se falhar, salvar offline
        await salvarPontoOffline(pontoData);
      }
    } else {
      // Salvar offline
      await salvarPontoOffline(pontoData);
      const status = await getStatusOffline(config.empresaId);
      setStatusOffline(status);
    }

    setLastPunch({
      nome: empregado.nome,
      hora: agora.toLocaleTimeString('pt-BR'),
      tipo: 'ponto'
    });
    
    toast.success(`Ponto registrado para ${empregado.nome}!`);
  };

  const formatTime = (date) => {
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${date.toLocaleTimeString('pt-BR')},${ms}`;
  };

  if (modelError) {
    const handleForceUpdate = async () => {
      try {
        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map(name => caches.delete(name)));
        }
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(reg => reg.unregister()));
        }
        if ('indexedDB' in window) {
          indexedDB.deleteDatabase('tensorflowjs');
        }
        window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
      } catch (err) {
        window.location.reload(true);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-red-900/50 border-red-700">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Erro ao carregar sistema de biometria</h3>
            <p className="text-red-200 mb-6 text-sm">{modelError}</p>
            
            <div className="space-y-3">
              <Button onClick={handleForceUpdate} className="w-full bg-amber-600 hover:bg-amber-700">
                <RotateCcw className="w-4 h-4 mr-2" /> Forçar Atualização (Recomendado)
              </Button>
              
              <Button onClick={loadModels} variant="outline" className="w-full border-red-600 text-red-300 hover:bg-red-600/20">
                <RefreshCw className="w-4 h-4 mr-2" /> Tentar novamente
              </Button>
              
              <Button onClick={onVoltar} variant="ghost" className="w-full text-slate-400" data-testid="btn-voltar-menu-erro">
                <ChevronLeft className="w-4 h-4 mr-2" /> Voltar ao menu
              </Button>
            </div>
            
            <p className="text-xs text-red-300/70 mt-4">
              Se o problema persistir após a atualização, tente abrir em uma aba anônima (Ctrl+Shift+N)
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de seleção de dispositivo
  if (showDeviceSelector) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800/90 border-slate-700">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">JORNYX Ponto</h1>
              <p className="text-slate-400">{config.empresaNome}</p>
            </div>
            
            <p className="text-center text-slate-300 mb-6">
              Selecione o tipo de dispositivo:
            </p>
            
            <div className="space-y-4">
              <Button
                onClick={() => handleSelectDevice(true)}
                className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white text-lg"
                data-testid="btn-select-celular"
              >
                <Smartphone className="w-6 h-6 mr-3" />
                Celular
              </Button>
              
              <Button
                onClick={() => handleSelectDevice(false)}
                className="w-full h-16 bg-purple-600 hover:bg-purple-700 text-white text-lg"
                data-testid="btn-select-desktop"
              >
                <Monitor className="w-6 h-6 mr-3" />
                Desktop / Tablet
              </Button>
            </div>
            
            <Button 
              onClick={onVoltar} 
              variant="ghost" 
              className="w-full mt-6 text-slate-400"
              data-testid="btn-voltar-selecao"
            >
              <ChevronLeft className="w-4 h-4 mr-2" /> Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {isMobile ? (
      /* ===== MOBILE: Câmera Fullscreen ===== */
      <div className="flex flex-col h-full">
        {/* Header: Ícones à esquerda, Empresa + Relógio à direita */}
        <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between p-3 bg-gradient-to-b from-black/80 to-transparent">
          {/* Ícones à esquerda */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" onClick={trocarCamera} className="text-white/80 p-2 h-auto" data-testid="btn-trocar-camera-ponto">
              <RotateCcw className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setShowLogs(!showLogs)} 
              className="text-white/80 p-2 h-auto"
              data-testid="btn-toggle-logs-ponto"
            >
              <FileText className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Empresa + Relógio à direita */}
          <div className="text-right">
            <p className="text-sm text-slate-300">{config.empresaNome}</p>
            <div className="font-mono text-emerald-400 text-2xl font-bold">
              {formatTime(currentTime)}
            </div>
          </div>
        </div>

        {/* Logs de Diagnóstico (quando ativo) */}
        {showLogs && pontoLogs.length > 0 && (
          <div className="absolute top-20 left-3 right-3 z-20 bg-slate-900/95 rounded-lg p-2 max-h-32 overflow-y-auto">
            {pontoLogs.slice(-8).map((log, i) => (
              <div key={i} className={`text-xs font-mono ${log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-emerald-400' : 'text-slate-400'} truncate`}>
                {log.time.split(':').slice(1).join(':')} {log.msg.substring(0, 40)}
              </div>
            ))}
          </div>
        )}

        {/* Câmera Fullscreen */}
        <div className="flex-1 relative bg-black overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            data-testid="video-ponto-mobile"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ 
              transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
              WebkitTransform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
            }}
            onLoadedData={() => addLog('Video mobile onLoadedData', 'success')}
            onCanPlay={() => addLog('Video mobile onCanPlay', 'success')}
            onError={(e) => addLog(`Video mobile erro: ${e.target?.error?.message || 'desconhecido'}`, 'error')}
          />
          
          {/* Indicador de câmera não iniciada */}
          {!cameraReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-10">
              <div className="text-center">
                <Loader2 className="w-16 h-16 animate-spin text-emerald-400 mx-auto mb-4" />
                <p className="text-white text-lg">Iniciando câmera...</p>
                <p className="text-slate-400 text-sm mt-2">Permita o acesso à câmera</p>
                <button 
                  onClick={() => startCamera(facingMode)}
                  className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm"
                >
                  Tentar Novamente
                </button>
              </div>
            </div>
          )}
          
          {/* Borda de detecção */}
          <div className={`absolute inset-0 border-8 transition-colors duration-300 pointer-events-none ${
            faceDetected ? 'border-emerald-400/70' : 'border-transparent'
          } ${processing ? 'border-amber-400 animate-pulse' : ''}`} />
          
          {/* Botão BATER PONTO - Canto inferior esquerdo */}
          <div className="absolute bottom-6 left-4 z-20">
            <Button
              onClick={handleRegistrarPonto}
              disabled={!modelsLoaded || !cameraReady || processing}
              className={`h-16 px-6 text-lg font-bold rounded-xl shadow-lg transition-all ${
                faceDetected 
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white animate-pulse' 
                  : 'bg-slate-700/90 text-slate-300'
              }`}
              data-testid="btn-registrar-ponto-mobile"
            >
              {processing ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <ScanFace className="w-6 h-6 mr-2" />
                  BATER PONTO
                </>
              )}
            </Button>
          </div>
          
          {/* Last punch overlay */}
          {lastPunch && (
            <div className="absolute bottom-20 left-4 right-4 z-20">
              <div className="bg-emerald-600/95 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-white/80 text-xs">Ponto registrado com sucesso!</p>
                    <p className="text-white text-lg font-bold">{lastPunch.nome}</p>
                    <p className="text-emerald-100 text-sm">{lastPunch.hora}</p>
                  </div>
                </div>
                <Button
                  onClick={() => setLastPunch(null)}
                  className="w-full h-12 bg-white/20 hover:bg-white/30 text-white font-semibold"
                  data-testid="btn-nova-batida-mobile"
                >
                  <ScanFace className="w-5 h-5 mr-2" />
                  NOVA BATIDA
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      ) : (
      /* ===== DESKTOP: Layout com câmera grande ===== */
      <div className="flex flex-col h-full">
        {/* Header Desktop */}
        <div className="flex items-center justify-between px-6 py-4 bg-slate-900/90 border-b border-slate-700">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onVoltar} className="text-slate-400 hover:text-white" data-testid="btn-voltar-ponto-desktop">
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">JORNYX</h1>
              <p className="text-sm text-slate-400">{config.empresaNome}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Trocar Câmera */}
            <Button variant="outline" onClick={trocarCamera} className="border-slate-600 text-slate-300" data-testid="btn-trocar-camera-ponto-desktop">
              <RotateCcw className="w-5 h-5 mr-2" />
              {facingMode === 'user' ? 'Frontal' : 'Traseira'}
            </Button>
            
            {/* Status */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm ${online ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
              {online ? <Cloud className="w-4 h-4" /> : <CloudOff className="w-4 h-4" />}
              {online ? 'Online' : 'Offline'}
            </div>
            
            {statusOffline?.biometrias_offline > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/20 text-blue-400 text-sm">
                <Database className="w-4 h-4" />
                {statusOffline.biometrias_offline} locais
              </div>
            )}
            
            <div className="text-right">
              <div className="text-3xl font-bold text-white font-mono">
                {formatTime(currentTime)}
              </div>
              <div className="text-sm text-slate-400">
                {currentTime.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'short' })}
              </div>
            </div>
            
            <div className={`px-4 py-2 rounded-lg text-sm font-medium ${online ? 'bg-emerald-500' : 'bg-amber-500'} text-white`}>
              {config.modoRep}
            </div>
          </div>
        </div>

        {/* Área Principal Desktop */}
        <div className="flex-1 flex">
          {/* Câmera Grande */}
          <div className="flex-1 relative bg-black overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              data-testid="video-ponto-desktop"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ 
                transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                WebkitTransform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
              }}
              onLoadedData={() => addLog('Video desktop onLoadedData', 'success')}
              onError={(e) => addLog(`Video desktop erro: ${e.target?.error?.message}`, 'error')}
            />
            
            {/* Indicador de câmera não iniciada */}
            {!cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-10">
                <div className="text-center">
                  <Loader2 className="w-20 h-20 animate-spin text-emerald-400 mx-auto mb-4" />
                  <p className="text-white text-2xl">Iniciando câmera...</p>
                  <p className="text-slate-400 text-base mt-2">Permita o acesso à câmera se solicitado</p>
                </div>
              </div>
            )}
            
            {/* Borda de detecção */}
            <div className={`absolute inset-0 border-8 transition-colors duration-300 pointer-events-none ${
              faceDetected ? 'border-emerald-400/70' : 'border-transparent'
            } ${processing ? 'border-amber-400 animate-pulse' : ''}`} />
            
            {/* Status do Sistema Desktop */}
            <div className="absolute top-6 left-6 z-20 space-y-2">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${modelsLoaded ? 'bg-emerald-500/90 text-white' : 'bg-amber-500/90 text-white animate-pulse'}`}>
                {modelsLoaded ? <CheckCircle className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                {modelsLoaded ? 'IA Pronta' : 'Carregando IA...'}
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${cameraReady ? 'bg-emerald-500/90 text-white' : 'bg-amber-500/90 text-white animate-pulse'}`}>
                {cameraReady ? <Camera className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                {cameraReady ? 'Câmera Ativa' : 'Iniciando Câmera...'}
              </div>
            </div>
            
            {/* Logs de Diagnóstico Desktop */}
            {pontoLogs.length > 0 && (
              <div data-testid="log-ponto-desktop" className="absolute top-6 right-6 z-20 max-w-md">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowLogs(!showLogs)}
                  className="bg-slate-900/80 hover:bg-slate-800 text-slate-300 text-sm px-4"
                  data-testid="btn-toggle-logs-ponto-desktop"
                >
                  {showLogs ? 'Ocultar Logs' : `Ver Logs (${pontoLogs.length})`}
                </Button>
                {showLogs && (
                  <div className="bg-slate-900/95 rounded-lg p-3 max-h-60 overflow-y-auto text-sm font-mono mt-2 shadow-xl border border-slate-700">
                    {pontoLogs.slice(-15).map((log, i) => (
                      <div key={i} className={`${log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-emerald-400' : 'text-slate-400'}`}>
                        {log.time} - {log.msg}
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const logText = pontoLogs.map(l => `${l.time} [${l.type}] ${l.msg}`).join('\n');
                        navigator.clipboard.writeText(logText);
                        toast.success('Logs copiados!');
                      }}
                      className="mt-2 text-xs text-blue-400 w-full h-7"
                    >
                      Copiar Logs
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Status message - footer */}
            <div 
              onClick={handleRegistrarPonto}
              className={`absolute bottom-0 left-0 right-0 py-8 text-center text-xl font-medium cursor-pointer transition-all ${
                faceDetected ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-slate-900/95 text-slate-300'
              } ${processing ? 'pointer-events-none' : ''}`}
              data-testid="btn-registrar-ponto-desktop"
            >
              {!modelsLoaded ? (
                <span className="flex items-center justify-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin" /> Carregando sistema...
                </span>
              ) : !cameraReady ? (
                <span className="flex items-center justify-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin" /> Iniciando câmera...
                </span>
              ) : processing ? (
                <span className="flex items-center justify-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin" /> Processando...
                </span>
              ) : faceDetected ? (
                <span className="flex items-center justify-center gap-3 animate-pulse">
                  <ScanFace className="w-8 h-8" /> CLIQUE AQUI PARA REGISTRAR PONTO
                </span>
              ) : detectionMessage}
            </div>
            
            {/* Last punch overlay Desktop */}
            {lastPunch && (
              <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 min-w-[400px]">
                <div className="bg-emerald-600/95 rounded-2xl p-6 shadow-2xl space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                    <div>
                      <p className="text-white/80 text-sm">Ponto registrado com sucesso!</p>
                      <p className="text-white text-2xl font-bold">{lastPunch.nome}</p>
                      <p className="text-emerald-100 text-lg">{lastPunch.hora}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setLastPunch(null)}
                    className="w-full h-14 bg-white/20 hover:bg-white/30 text-white font-semibold text-lg"
                    data-testid="btn-nova-batida-desktop"
                  >
                    <ScanFace className="w-6 h-6 mr-2" />
                    NOVA BATIDA
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

// ==================== TELA DE LOGIN ADMIN ====================
const TelaLoginAdmin = ({ config, onLoginSuccess, onVoltar }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha email e senha');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();
      const token = data.access_token || data.token;
      const usuario = data.user || data.usuario;

      if (res.ok && token && usuario) {
        const perfil = usuario.perfil || usuario.tipo;
        const papelEmpregado = usuario.papel_empregado;
        const isLider = usuario.is_lider;
        
        // Perfis de usuário que têm acesso direto
        const perfisPermitidos = [
          'administrador_empresa', 
          'admin_empresa',
          'admin',  // Perfil legado/simplificado
          'rh', 
          'admin_plataforma',
          'administrador_plataforma',
          'administrador_escritorio',
          'analista_escritorio',
          'auxiliar_escritorio'
        ];
        
        // Papéis de empregado que dão acesso administrativo
        const papeisPermitidos = ['rh', 'admin', 'gestor', 'lider'];
        
        // Verificar se tem acesso: perfil direto OU papel de empregado OU é líder
        const temAcessoPorPerfil = perfisPermitidos.includes(perfil);
        const temAcessoPorPapel = papelEmpregado && papeisPermitidos.includes(papelEmpregado);
        const temAcessoPorLideranca = isLider;
        
        if (!temAcessoPorPerfil && !temAcessoPorPapel && !temAcessoPorLideranca) {
          toast.error('Acesso permitido apenas para administradores, RH, gestores ou líderes');
          return;
        }

        // Para usuários de escritório, verificar se a empresa pertence ao escritório
        const isEscritorio = ['administrador_escritorio', 'analista_escritorio', 'auxiliar_escritorio'].includes(perfil);
        const isAdminPlataforma = ['admin_plataforma', 'administrador_plataforma'].includes(perfil);
        const isEmpregadoComPapel = temAcessoPorPapel || temAcessoPorLideranca;
        
        // Empregados com papel especial só podem acessar sua própria empresa
        if (isEmpregadoComPapel && usuario.empresa_id !== config.empresaId) {
          toast.error(`Seu usuário pertence a outra empresa. Selecione a empresa "${config.empresaNome}" na tela inicial.`);
          return;
        }
        
        // Usuários de escritório: verificar se empresa pertence ao escritório OU se tem vínculo direto
        if (isEscritorio) {
          // Verificar via API se o usuário tem acesso a esta empresa
          try {
            const checkAccess = await fetch(`${API_URL}/api/usuarios/verificar-acesso-empresa/${config.empresaId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!checkAccess.ok) {
              toast.error(`Você não tem acesso à empresa "${config.empresaNome}". Verifique se a empresa está vinculada ao seu escritório.`);
              return;
            }
          } catch (e) {
            console.error('Erro ao verificar acesso:', e);
            toast.error('Erro ao verificar permissões. Tente novamente.');
            return;
          }
        }
        
        // Outros perfis (não escritório, não plataforma, não empregado com papel)
        if (!isEscritorio && !isAdminPlataforma && !isEmpregadoComPapel && usuario.empresa_id !== config.empresaId) {
          toast.error(`Seu usuário pertence a outra empresa. Selecione a empresa correta na tela inicial ou use um administrador da "${config.empresaNome}".`);
          return;
        }

        const adminConfig = {
          ...config,
          token: token,
          usuarioId: usuario.id,
          usuarioNome: usuario.nome,
          isAdmin: true
        };

        toast.success(`Bem-vindo, ${usuario.nome}!`);
        onLoginSuccess(adminConfig);
      } else {
        toast.error(data.detail || 'Email ou senha incorretos');
      }
    } catch (err) {
      console.error('Erro no login:', err);
      toast.error('Erro ao conectar ao servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-slate-800/90 border-slate-700 shadow-2xl">
        <CardHeader className="text-center border-b border-slate-700 pb-6">
          <Button
            variant="ghost"
            onClick={onVoltar}
            className="absolute left-4 top-4 text-slate-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
            <Key className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-xl font-bold text-white">Acesso Administrativo</CardTitle>
          <CardDescription className="text-slate-400">{config.empresaNome}</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <Input
                type="email"
                placeholder="admin@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="admin-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Senha</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white pr-10"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
              data-testid="admin-login-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <LogIn className="w-5 h-5 mr-2" />}
              Entrar
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== PAINEL DE CADASTRO OTIMIZADO ====================
const PainelCadastro = ({ config, onVoltar, onLogout }) => {
  const [empregados, setEmpregados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEmpregado, setShowAddEmpregado] = useState(false);
  const [selectedEmpregado, setSelectedEmpregado] = useState(null);
  const [showBiometriaModal, setShowBiometriaModal] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [capturando, setCapturando] = useState(false);
  const [capturaProgress, setCapturaProgress] = useState({ atual: 0, total: 5 });
  const [autoCaptura, setAutoCaptura] = useState(true);
  const [biometriaLogs, setBiometriaLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [faceDetected, setFaceDetected] = useState(false);
  const [isMobile, setIsMobile] = useState(null);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const capturaIntervalRef = useRef(null);
  const detectionIntervalRef = useRef(null);

  // Função para adicionar log
  const addLog = (msg, type = 'info') => {
    const time = new Date().toLocaleTimeString('pt-BR');
    setBiometriaLogs(prev => [...prev, { time, msg, type }].slice(-50));
    console.log(`[${type}] ${msg}`);
  };

  // Função para selecionar dispositivo e iniciar biometria
  const handleSelectDeviceAndStart = (mobile) => {
    setIsMobile(mobile);
    setShowDeviceSelector(false);
    setShowBiometriaModal(true);
    addLog(`Dispositivo selecionado: ${mobile ? 'Celular' : 'Desktop/Tablet'}`, 'info');
  };

  // Função para abrir modal de biometria (mostra seleção de dispositivo primeiro)
  const openBiometriaModal = (empregado, reiniciar = false) => {
    openBiometriaWithDeviceSelect(empregado, reiniciar);
  };

  const [novoEmpregado, setNovoEmpregado] = useState({
    nome: '',
    cpf: '',
    matricula: '',
    setor: '',
    localidade: ''
  });

  useEffect(() => {
    fetchEmpregados();
    loadModelsWithLogs();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (capturaIntervalRef.current) {
        clearInterval(capturaIntervalRef.current);
      }
    };
  }, []);

  const loadModelsWithLogs = async () => {
    try {
      addLog('Iniciando carregamento dos modelos OTIMIZADOS...', 'info');
      
      // Verificar backend do TensorFlow.js
      const tf = faceapi.tf;
      const backend = tf.getBackend();
      addLog(`TensorFlow.js backend inicial: ${backend || 'não inicializado'}`, 'info');
      
      // Tentar forçar WebGL se disponível
      if (!backend || backend === 'cpu') {
        try {
          await tf.setBackend('webgl');
          await tf.ready();
          addLog(`Backend alterado para: ${tf.getBackend()}`, 'success');
        } catch (backendErr) {
          addLog(`Não foi possível usar WebGL: ${backendErr.message}`, 'warning');
        }
      }
      
      // URL do CDN externo (jsDelivr) - mais confiável que servidor local
      const CDN_MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.13/model';
      const LOCAL_MODEL_URL = `${window.location.origin}/models`;
      
      // Tentar primeiro o CDN, fallback para local
      let MODEL_URL = CDN_MODEL_URL;
      
      addLog(`Tentando CDN: ${CDN_MODEL_URL}`, 'info');
      
      // Verificar se CDN está acessível
      try {
        const cdnCheck = await fetch(`${CDN_MODEL_URL}/tiny_face_detector_model-weights_manifest.json`, { 
          method: 'HEAD',
          mode: 'cors'
        });
        if (!cdnCheck.ok) {
          addLog(`CDN não acessível (${cdnCheck.status}), usando local`, 'warn');
          MODEL_URL = LOCAL_MODEL_URL;
        } else {
          addLog('CDN acessível ✓', 'success');
        }
      } catch (cdnErr) {
        addLog(`CDN falhou: ${cdnErr.message}, usando local`, 'warn');
        MODEL_URL = LOCAL_MODEL_URL;
      }
      
      addLog(`URL final: ${MODEL_URL}`, 'info');
      
      // OTIMIZAÇÃO: Carregar modelos TINY para máxima velocidade
      addLog('Carregando TinyFaceDetector (detecção ultra-rápida)...', 'info');
      await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
      addLog('TinyFaceDetector carregado ✓', 'success');
      
      addLog('Carregando Face Landmark TINY...', 'info');
      await faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL);
      addLog('Face Landmark Tiny carregado ✓', 'success');
      
      addLog('Carregando Face Recognition...', 'info');
      await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
      addLog('Face Recognition carregado ✓', 'success');
      
      setModelsLoaded(true);
      addLog(`TODOS OS MODELOS CARREGADOS! Backend: ${tf.getBackend()}`, 'success');
    } catch (err) {
      addLog(`ERRO CRÍTICO: ${err.message}`, 'error');
      if (err.stack) {
        addLog(`Stack: ${err.stack.substring(0, 200)}`, 'error');
      }
      console.error('Erro ao carregar modelos:', err);
      toast.error('Erro ao carregar sistema de biometria. Veja os logs para detalhes.');
    }
  };

  const loadModels = loadModelsWithLogs;

  const fetchEmpregados = async () => {
    try {
      const res = await fetch(`${API_URL}/api/empresas-ponto/empregados-totem/${config.empresaId}`, {
        headers: { 'Authorization': `Bearer ${config.token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setEmpregados(data.empregados || []);
      }
    } catch (err) {
      console.error('Erro ao buscar empregados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmpregado = async (e) => {
    e.preventDefault();
    if (!novoEmpregado.nome || !novoEmpregado.cpf) {
      toast.error('Nome e CPF são obrigatórios');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/empresas-ponto/empregados-totem`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${config.token}`
        },
        body: JSON.stringify({
          empresa_id: config.empresaId,
          nome: novoEmpregado.nome,
          cpf: novoEmpregado.cpf,
          matricula: novoEmpregado.matricula || null,
          setor: novoEmpregado.setor || 'geral',
          localidade: novoEmpregado.localidade || 'matriz'
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Empregado cadastrado! Agora cadastre a biometria.');
        setNovoEmpregado({ nome: '', cpf: '', matricula: '', setor: '', localidade: '' });
        setShowAddEmpregado(false);
        fetchEmpregados();
        
        // Abrir modal de biometria automaticamente
        if (data.empregado) {
          setTimeout(() => openBiometriaModal(data.empregado), 500);
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao cadastrar');
      }
    } catch (err) {
      toast.error('Erro ao cadastrar empregado');
    }
  };

  const handleDeleteEmpregado = async (empregadoId) => {
    if (!window.confirm('Deseja excluir este empregado e suas biometrias?')) return;

    try {
      const res = await fetch(`${API_URL}/api/empresas-ponto/empregados-totem/${empregadoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${config.token}` }
      });

      if (res.ok) {
        toast.success('Empregado excluído');
        fetchEmpregados();
      }
    } catch (err) {
      toast.error('Erro ao excluir');
    }
  };

  const startCamera = async (mode = facingMode) => {
    addLog(`Biometria: Iniciando câmera ${mode}...`, 'info');
    addLog(`Modo dispositivo: ${isMobile ? 'Celular' : 'Desktop/Tablet'}`, 'info');
    
    try {
      // Parar câmera anterior se existir
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Usar isMobile do estado (selecionado pelo usuário)
      // PADRÃO BIOMÉTRICO: Proporção 4:3 (640x480) ou 3:4 para retrato mobile
      const constraints = {
        video: {
          facingMode: mode,
          width: { ideal: isMobile ? 480 : 640 },
          height: { ideal: isMobile ? 640 : 480 },
          aspectRatio: { ideal: isMobile ? 0.75 : 1.333 }
        },
        audio: false
      };
      
      addLog(`Constraints: ${isMobile ? '480x640 (3:4)' : '640x480 (4:3)'}`, 'info');
      
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (constraintErr) {
        addLog(`Constraints falharam: ${constraintErr.message}`, 'warning');
        addLog('Tentando com constraints básicas...', 'info');
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: mode }, 
          audio: false 
        });
      }
      
      addLog(`Stream obtido: ${stream.getVideoTracks().length} track(s)`, 'success');
      
      // Log das configurações reais da câmera
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        addLog(`Resolução obtida: ${settings.width}x${settings.height}`, 'success');
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        
        addLog('srcObject atribuído ao video element', 'info');
        
        // Handlers de vídeo
        const handleVideoReady = () => {
          const v = videoRef.current;
          if (v && v.videoWidth > 0) {
            addLog(`Vídeo renderizando: ${v.videoWidth}x${v.videoHeight}`, 'success');
            startFaceDetection();
          }
        };
        
        videoRef.current.onloadeddata = handleVideoReady;
        videoRef.current.oncanplay = handleVideoReady;
        
        // Forçar play()
        try {
          await videoRef.current.play();
          addLog('play() executado', 'success');
        } catch (playErr) {
          addLog(`play() erro: ${playErr.message}`, 'warning');
        }
        
        // Fallback: verificar periodicamente
        let attempts = 0;
        const checkInterval = setInterval(() => {
          attempts++;
          const v = videoRef.current;
          if (v && v.videoWidth > 0) {
            addLog(`Vídeo OK (${v.videoWidth}x${v.videoHeight})`, 'success');
            clearInterval(checkInterval);
            startFaceDetection();
          }
          if (attempts >= 10) {
            clearInterval(checkInterval);
            addLog('Timeout aguardando vídeo', 'error');
          }
        }, 500);
      }
      
      addLog('Câmera biometria iniciada', 'success');
    } catch (err) {
      addLog(`Erro câmera: ${err.name} - ${err.message}`, 'error');
      toast.error('Erro ao acessar câmera');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (capturaIntervalRef.current) {
      clearInterval(capturaIntervalRef.current);
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }
  };

  // Trocar câmera frontal/traseira
  const trocarCamera = () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    startCamera(newMode);
  };

  // Detecção facial em tempo real (feedback visual) - OTIMIZADO com TinyFaceDetector
  const startFaceDetection = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    // TinyFaceDetector é ~10x mais rápido que SSD MobileNet
    const tinyOptions = new faceapi.TinyFaceDetectorOptions({ 
      inputSize: 224,
      scoreThreshold: 0.4
    });

    detectionIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || !modelsLoaded || capturando) return;
      
      if (videoRef.current.readyState < 2) return;

      try {
        const detection = await faceapi
          .detectSingleFace(videoRef.current, tinyOptions);

        setFaceDetected(!!detection);
      } catch (err) {
        // Silenciar erros de detecção
      }
    }, 150); // Mais rápido (era 300ms)
  };

  // Abre seleção de dispositivo antes do modal de biometria
  const openBiometriaWithDeviceSelect = (empregado, reiniciar = false) => {
    setSelectedEmpregado(empregado);
    setBiometriaLogs([]);
    setShowLogs(false);
    setFaceDetected(false);
    
    // Se reiniciar ou se já tem 5 amostras, resetar para 0
    if (reiniciar || (empregado.total_amostras || 0) >= 5) {
      setCapturaProgress({ atual: 0, total: 5 });
      if ((empregado.total_amostras || 0) >= 5) {
        limparBiometriasEmpregado(empregado.id);
      }
    } else {
      setCapturaProgress({ atual: empregado.total_amostras || 0, total: 5 });
    }
    
    // Mostrar seleção de dispositivo
    setShowDeviceSelector(true);
  };

  // Após selecionar dispositivo, abre o modal
  const handleSelectDeviceForBiometria = (mobile) => {
    setIsMobile(mobile);
    setShowDeviceSelector(false);
    setShowBiometriaModal(true);
    addLog(`Dispositivo: ${mobile ? 'Celular' : 'Desktop/Tablet'}`, 'info');
    setTimeout(() => startCamera(facingMode), 500);
  };

  // Função para limpar biometrias existentes (para recadastro)
  const limparBiometriasEmpregado = async (empregadoId) => {
    try {
      const res = await fetch(`${API_URL}/api/empresas-ponto/limpar-biometria/${config.empresaId}/${empregadoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${config.token}` }
      });
      if (res.ok) {
        addLog('Biometrias anteriores limpas para recadastro', 'info');
      }
    } catch (err) {
      addLog(`Erro ao limpar biometrias: ${err.message}`, 'error');
    }
  };

  const closeBiometriaModal = () => {
    stopCamera();
    setSelectedEmpregado(null);
    setShowBiometriaModal(false);
    setCapturaProgress({ atual: 0, total: 5 });
    setFaceDetected(false);
  };

  // ==================== CAPTURA OTIMIZADA EM LOTE ====================
  const iniciarCapturaAutomatica = async () => {
    if (!videoRef.current || !modelsLoaded || !selectedEmpregado) return;
    
    setCapturando(true);
    let capturas = 0;
    const maxCapturas = 5 - (selectedEmpregado.total_amostras || 0);
    
    toast.info(`Iniciando captura automática. Mantenha o rosto estável.`);

    // OTIMIZAÇÃO: Usar apenas modelos Tiny para velocidade
    const tinyOptions = new faceapi.TinyFaceDetectorOptions({ 
      inputSize: 224,      // Balanceado entre velocidade e precisão
      scoreThreshold: 0.5
    });

    capturaIntervalRef.current = setInterval(async () => {
      if (capturas >= maxCapturas) {
        clearInterval(capturaIntervalRef.current);
        setCapturando(false);
        toast.success('Biometria completa!');
        closeBiometriaModal();
        fetchEmpregados();
        return;
      }

      try {
        // Usar TinyFaceDetector + TinyLandmarks para extração rápida
        const detection = await faceapi
          .detectSingleFace(videoRef.current, tinyOptions)
          .withFaceLandmarks(true)  // true = usar faceLandmark68TinyNet
          .withFaceDescriptor();

        if (detection) {
          const embedding = Array.from(detection.descriptor);
          
          // Enviar para API
          const res = await fetch(`${API_URL}/api/empresas-ponto/cadastrar-biometria`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${config.token}`
            },
            body: JSON.stringify({
              empresa_id: config.empresaId,
              empregado_id: selectedEmpregado.id,
              embedding: embedding
            })
          });

          if (res.ok) {
            capturas++;
            setCapturaProgress(prev => ({ ...prev, atual: (selectedEmpregado.total_amostras || 0) + capturas }));
            toast.success(`Captura ${capturas}/${maxCapturas} realizada`);
          }
        }
      } catch (err) {
        console.error('Erro na captura:', err);
      }
    }, 500); // Captura a cada 500ms (mais rápido)
  };

  const capturarBiometriaManual = async () => {
    if (!videoRef.current || !modelsLoaded || !selectedEmpregado) return;
    
    setCapturando(true);
    const startTime = performance.now();
    
    try {
      // OTIMIZAÇÃO: Usar apenas modelos Tiny
      const tinyOptions = new faceapi.TinyFaceDetectorOptions({ 
        inputSize: 224,
        scoreThreshold: 0.5
      });
      
      // Detecção + Landmarks + Embedding em uma chamada
      const detection = await faceapi
        .detectSingleFace(videoRef.current, tinyOptions)
        .withFaceLandmarks(true)  // true = usar faceLandmark68TinyNet
        .withFaceDescriptor();

      if (!detection) {
        toast.error('Nenhum rosto detectado');
        setCapturando(false);
        return;
      }

      const embedding = Array.from(detection.descriptor);
      const processTime = performance.now() - startTime;
      addLog(`Embedding extraído em ${processTime.toFixed(0)}ms`, 'success');

      const res = await fetch(`${API_URL}/api/empresas-ponto/cadastrar-biometria`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${config.token}`
        },
        body: JSON.stringify({
          empresa_id: config.empresaId,
          empregado_id: selectedEmpregado.id,
          embedding: embedding
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Biometria cadastrada! (${data.total_amostras}/5)`);
        setCapturaProgress({ atual: data.total_amostras, total: 5 });
        fetchEmpregados();
        
        if (data.total_amostras >= 5) {
          closeBiometriaModal();
        }
      } else {
        toast.error('Erro ao cadastrar biometria');
      }
    } catch (err) {
      toast.error('Erro ao capturar biometria');
    } finally {
      setCapturando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onVoltar} className="text-slate-400 hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Cadastro de Funcionários</h1>
              <p className="text-sm text-slate-400">{config.empresaNome}</p>
            </div>
          </div>
          <Button variant="outline" onClick={onLogout} className="border-slate-600 text-slate-300">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>

        {/* Add Employee Button */}
        <div className="mb-6">
          <Button
            onClick={() => setShowAddEmpregado(!showAddEmpregado)}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Novo Funcionário
          </Button>
        </div>

        {/* Add Employee Form */}
        {showAddEmpregado && (
          <Card className="mb-6 bg-slate-800/90 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Cadastrar Funcionário</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddEmpregado} className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-sm font-medium text-slate-300 mb-2">Nome *</label>
                  <Input
                    data-testid="input-nome-empregado"
                    value={novoEmpregado.nome}
                    onChange={(e) => setNovoEmpregado({...novoEmpregado, nome: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="Nome completo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">CPF *</label>
                  <Input
                    data-testid="input-cpf-empregado"
                    value={novoEmpregado.cpf}
                    onChange={(e) => setNovoEmpregado({...novoEmpregado, cpf: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="000.000.000-00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Matrícula</label>
                  <Input
                    data-testid="input-matricula-empregado"
                    value={novoEmpregado.matricula}
                    onChange={(e) => setNovoEmpregado({...novoEmpregado, matricula: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Setor</label>
                  <Input
                    data-testid="input-setor-empregado"
                    value={novoEmpregado.setor}
                    onChange={(e) => setNovoEmpregado({...novoEmpregado, setor: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="Ex: Produção, Administrativo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Localidade</label>
                  <Input
                    data-testid="input-localidade-empregado"
                    value={novoEmpregado.localidade}
                    onChange={(e) => setNovoEmpregado({...novoEmpregado, localidade: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    placeholder="Ex: Matriz, Filial SP"
                  />
                </div>
                <div className="col-span-2 flex gap-2">
                  <Button data-testid="btn-cadastrar-empregado" type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle className="w-4 h-4 mr-2" /> Cadastrar e Coletar Biometria
                  </Button>
                  <Button data-testid="btn-cancelar-cadastro" type="button" variant="outline" onClick={() => setShowAddEmpregado(false)} className="border-slate-600 text-slate-300">
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Employee List */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              Funcionários Cadastrados ({empregados.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
              </div>
            ) : empregados.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum funcionário cadastrado</p>
                <p className="text-sm">Clique em &quot;Novo Funcionário&quot; para começar</p>
              </div>
            ) : (
              <div className="space-y-3">
                {empregados.map(emp => (
                  <div key={emp.id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${emp.tem_biometria ? 'bg-emerald-500/20' : 'bg-slate-600'}`}>
                        {emp.tem_biometria ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <User className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">{emp.nome}</p>
                        <p className="text-sm text-slate-400">
                          CPF: {emp.cpf} {emp.setor && `• ${emp.setor}`}
                        </p>
                        <p className="text-xs text-slate-500">
                          {emp.tem_biometria ? `${emp.total_amostras || 0}/5 amostras` : 'Sem biometria'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        data-testid={`btn-biometria-${emp.id}`}
                        size="sm"
                        onClick={() => openBiometriaModal(emp, false)}
                        className={emp.tem_biometria && (emp.total_amostras || 0) >= 5 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                      >
                        <ScanFace className="w-4 h-4 mr-1" />
                        {emp.tem_biometria && (emp.total_amostras || 0) >= 5 ? 'Recadastrar' : emp.tem_biometria ? 'Continuar' : 'Cadastrar'}
                      </Button>
                      <Button
                        data-testid={`btn-excluir-${emp.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteEmpregado(emp.id)}
                        className="border-red-600 text-red-400 hover:bg-red-600/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Seleção de Dispositivo para Biometria */}
      {showDeviceSelector && selectedEmpregado && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Camera className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white mb-1">Cadastro de Biometria</h2>
                <p className="text-slate-400">{selectedEmpregado.nome}</p>
              </div>
              
              <p className="text-center text-slate-300 mb-6">
                Selecione o tipo de dispositivo:
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => handleSelectDeviceForBiometria(true)}
                  className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-lg"
                  data-testid="btn-biometria-celular"
                >
                  <Smartphone className="w-6 h-6 mr-3" />
                  Celular
                </Button>
                
                <Button
                  onClick={() => handleSelectDeviceForBiometria(false)}
                  className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-white text-lg"
                  data-testid="btn-biometria-desktop"
                >
                  <Monitor className="w-6 h-6 mr-3" />
                  Desktop / Tablet
                </Button>
              </div>
              
              <Button 
                onClick={() => {
                  setShowDeviceSelector(false);
                  setSelectedEmpregado(null);
                }} 
                variant="ghost" 
                className="w-full mt-4 text-slate-400"
              >
                Cancelar
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Biometria Modal - Usa isMobile para renderização condicional */}
      {showBiometriaModal && selectedEmpregado && (
        <div className="fixed inset-0 bg-slate-900 z-50 flex flex-col">
          
          {isMobile ? (
          /* ===== VERSÃO MOBILE: Layout Vertical ===== */
          <div className="flex flex-col h-full">
            {/* Header Mobile */}
            <div className="bg-slate-800 px-4 py-3 flex items-center justify-between border-b border-slate-700">
              <div>
                <h2 className="text-lg font-bold text-white">Cadastro de Biometria Facial</h2>
                <p className="text-sm text-slate-400">{selectedEmpregado.nome}</p>
              </div>
              <div className="flex items-center gap-2">
                {/* Seletor de Câmera Mobile */}
                <Button 
                  data-testid="btn-trocar-camera-mobile"
                  variant="ghost" 
                  onClick={trocarCamera} 
                  className="text-slate-300 p-2"
                  title={facingMode === 'user' ? 'Trocar para câmera traseira' : 'Trocar para câmera frontal'}
                >
                  <RotateCcw className="w-5 h-5" />
                </Button>
                <Button data-testid="btn-fechar-biometria-mobile" variant="ghost" onClick={closeBiometriaModal} className="text-slate-400 p-2">
                  <XCircle className="w-6 h-6" />
                </Button>
              </div>
            </div>

            {/* Progress Bar + Seletor de Câmera Mobile */}
            <div className="px-4 py-3 bg-slate-800/50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">Progresso</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${facingMode === 'user' ? 'bg-blue-500/30 text-blue-300' : 'bg-purple-500/30 text-purple-300'}`}>
                    {facingMode === 'user' ? 'Frontal' : 'Traseira'}
                  </span>
                </div>
                <span className="text-sm text-emerald-400 font-bold">{capturaProgress.atual}/{capturaProgress.total}</span>
              </div>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-2 rounded-full transition-colors ${i < capturaProgress.atual ? 'bg-emerald-500' : 'bg-slate-700'}`}
                  />
                ))}
              </div>
            </div>

            {/* Câmera Mobile - Área Principal */}
            <div className="flex-1 relative bg-black overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                data-testid="video-preview-mobile"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ 
                  transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                  WebkitTransform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
                }}
                onLoadedData={() => addLog('Video mobile onLoadedData', 'success')}
                onCanPlay={() => addLog('Video mobile onCanPlay', 'success')}
                onError={(e) => addLog(`Video mobile erro: ${e.target?.error?.message || 'desconhecido'}`, 'error')}
              />
              
              {/* Borda de detecção */}
              <div className={`absolute inset-0 border-8 transition-colors duration-300 pointer-events-none ${
                faceDetected ? 'border-emerald-400/70' : 'border-transparent'
              }`} />
              
              {/* Status de Detecção */}
              <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-sm font-medium ${
                faceDetected ? 'bg-emerald-500/90 text-white' : 'bg-slate-700/90 text-slate-300'
              }`}>
                {faceDetected ? '✓ Rosto detectado' : 'Posicione o rosto na câmera'}
              </div>
              
              {/* Overlay de Captura */}
              {capturando && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <div className="text-center">
                    <Loader2 className="w-16 h-16 animate-spin text-emerald-400 mx-auto mb-3" />
                    <p className="text-white text-lg font-medium">Capturando biometria...</p>
                    <p className="text-slate-300">Mantenha o rosto estável</p>
                  </div>
                </div>
              )}

              {/* Status do Modelo Mobile */}
              <div className={`absolute top-4 left-4 px-3 py-2 rounded-lg text-sm font-medium ${
                modelsLoaded ? 'bg-emerald-500/80 text-white' : 'bg-amber-500/80 text-white animate-pulse'
              }`}>
                {modelsLoaded ? '✓ Sistema pronto' : '⏳ Carregando...'}
              </div>

              {/* Logs de Debug Mobile */}
              {biometriaLogs.length > 0 && (
                <div data-testid="log-diagnostico-mobile" className="absolute top-4 right-4 max-w-[200px]">
                  <Button
                    data-testid="btn-toggle-logs-mobile"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowLogs(!showLogs)}
                    className="bg-slate-900/80 text-slate-300 text-xs"
                  >
                    {showLogs ? 'Ocultar' : `Ver Logs (${biometriaLogs.length})`}
                  </Button>
                  {showLogs && (
                    <div className="bg-slate-900/95 rounded-lg p-2 max-h-32 overflow-y-auto text-xs font-mono mt-1">
                      {biometriaLogs.slice(-8).map((log, i) => (
                        <div key={i} className={`${log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-emerald-400' : 'text-slate-400'} truncate`}>
                          {log.time.split(':').slice(1).join(':')} - {log.msg.substring(0, 30)}
                        </div>
                      ))}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const logText = biometriaLogs.map(l => `${l.time} [${l.type}] ${l.msg}`).join('\n');
                          navigator.clipboard.writeText(logText);
                          toast.success('Logs copiados!');
                        }}
                        className="mt-1 text-xs text-blue-400 w-full h-6"
                      >
                        Copiar Logs
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Botões Mobile - Footer Fixo */}
            <div className="bg-slate-800 px-4 py-4 space-y-3 border-t border-slate-700">
              <Button
                data-testid="btn-captura-automatica-mobile"
                onClick={iniciarCapturaAutomatica}
                disabled={capturando || capturaProgress.atual >= 5 || !modelsLoaded}
                className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-lg font-semibold"
              >
                <Zap className="w-6 h-6 mr-2" />
                Captura Rápida Automática
              </Button>
              
              <div className="flex gap-3">
                <Button
                  data-testid="btn-captura-manual-mobile"
                  onClick={capturarBiometriaManual}
                  disabled={capturando || capturaProgress.atual >= 5 || !modelsLoaded}
                  variant="outline"
                  className="flex-1 h-12 border-slate-600 text-slate-300"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Captura Manual
                </Button>
                <Button
                  data-testid="btn-fechar-modal-mobile"
                  onClick={closeBiometriaModal}
                  variant="outline"
                  className="h-12 border-slate-600 text-slate-300 px-6"
                >
                  Fechar
                </Button>
              </div>

              <p className="text-center text-sm text-slate-500">
                {modelsLoaded 
                  ? 'Posicione o rosto de frente para a câmera' 
                  : 'Carregando sistema de biometria...'}
              </p>
            </div>
          </div>
          ) : (
          /* ===== VERSÃO DESKTOP: Layout Horizontal ===== */
          <div className="flex flex-col w-full h-full">
            {/* Header Desktop */}
            <div className="bg-slate-800 px-6 py-4 flex items-center justify-between border-b border-slate-700">
              <div>
                <h2 className="text-xl font-bold text-white">Cadastro de Biometria Facial</h2>
                <p className="text-base text-slate-400 uppercase tracking-wide">{selectedEmpregado.nome}</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Seletor de Câmera Desktop */}
                <Button 
                  data-testid="btn-trocar-camera-desktop"
                  variant="outline"
                  onClick={trocarCamera} 
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  {facingMode === 'user' ? 'Câmera Frontal' : 'Câmera Traseira'}
                </Button>
                <Button data-testid="btn-fechar-biometria-desktop" variant="ghost" onClick={closeBiometriaModal} className="text-slate-400 hover:text-white p-2">
                  <XCircle className="w-7 h-7" />
                </Button>
              </div>
            </div>

            {/* Progress Bar Desktop */}
            <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-base text-slate-400">Progresso da captura</span>
                  <span className={`text-sm px-3 py-1 rounded-full ${facingMode === 'user' ? 'bg-blue-500/30 text-blue-300' : 'bg-purple-500/30 text-purple-300'}`}>
                    {facingMode === 'user' ? 'Câmera Frontal' : 'Câmera Traseira'}
                  </span>
                </div>
                <span className="text-lg text-emerald-400 font-bold">{capturaProgress.atual}/{capturaProgress.total}</span>
              </div>
              <div className="flex gap-2">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-3 rounded-full transition-colors ${i < capturaProgress.atual ? 'bg-emerald-500' : 'bg-slate-700'}`}
                  />
                ))}
              </div>
            </div>

            {/* Área Principal Desktop - Câmera Grande */}
            <div className="flex-1 relative bg-black overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                data-testid="video-preview-desktop"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ 
                  transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                  WebkitTransform: facingMode === 'user' ? 'scaleX(-1)' : 'none'
                }}
                onLoadedData={() => addLog('Video desktop onLoadedData', 'success')}
                onError={(e) => addLog(`Video desktop erro: ${e.target?.error?.message}`, 'error')}
              />
              
              {/* Borda de detecção */}
              <div className={`absolute inset-0 border-8 transition-colors duration-300 pointer-events-none ${
                faceDetected ? 'border-emerald-400/70' : 'border-transparent'
              }`} />
              
              {/* Status de Detecção */}
              <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full text-lg font-medium ${
                faceDetected ? 'bg-emerald-500/90 text-white' : 'bg-slate-700/90 text-slate-300'
              }`}>
                {faceDetected ? '✓ Rosto detectado - Pronto para capturar' : 'Posicione o rosto na câmera'}
              </div>
              
              {/* Overlay de Captura Desktop */}
              {capturando && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <div className="text-center">
                    <Loader2 className="w-20 h-20 animate-spin text-emerald-400 mx-auto mb-4" />
                    <p className="text-white text-2xl font-medium">Capturando biometria...</p>
                    <p className="text-slate-300 text-lg">Mantenha o rosto estável</p>
                  </div>
                </div>
              )}

              {/* Status do Modelo Desktop */}
              <div className={`absolute top-6 left-6 px-4 py-2 rounded-lg text-base font-medium ${
                modelsLoaded ? 'bg-emerald-500/90 text-white' : 'bg-amber-500/90 text-white animate-pulse'
              }`}>
                {modelsLoaded ? '✓ Sistema pronto' : '⏳ Carregando...'}
              </div>

              {/* Logs de Debug Desktop */}
              {biometriaLogs.length > 0 && (
                <div data-testid="log-diagnostico-desktop" className="absolute top-6 right-6 max-w-sm">
                  <Button
                    data-testid="btn-toggle-logs-desktop"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowLogs(!showLogs)}
                    className="bg-slate-900/80 hover:bg-slate-800 text-slate-300 text-sm px-4"
                  >
                    {showLogs ? 'Ocultar Logs' : `Ver Logs (${biometriaLogs.length})`}
                  </Button>
                  {showLogs && (
                    <div className="bg-slate-900/95 rounded-lg p-3 max-h-60 overflow-y-auto text-sm font-mono mt-2 shadow-xl border border-slate-700">
                      {biometriaLogs.slice(-12).map((log, i) => (
                        <div key={i} className={`${log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-emerald-400' : 'text-slate-400'}`}>
                          {log.time} - {log.msg}
                        </div>
                      ))}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const logText = biometriaLogs.map(l => `${l.time} [${l.type}] ${l.msg}`).join('\n');
                          navigator.clipboard.writeText(logText);
                          toast.success('Logs copiados!');
                        }}
                        className="mt-2 text-sm text-blue-400 w-full"
                      >
                        Copiar Logs
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Botões Desktop - Footer com mais espaço */}
            <div className="bg-slate-800 px-6 py-5 border-t border-slate-700">
              <div className="max-w-4xl mx-auto">
                <Button
                  data-testid="btn-captura-automatica-desktop"
                  onClick={iniciarCapturaAutomatica}
                  disabled={capturando || capturaProgress.atual >= 5 || !modelsLoaded}
                  className="w-full h-16 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-xl font-semibold mb-4"
                >
                  <Zap className="w-7 h-7 mr-3" />
                  Captura Rápida Automática
                </Button>
                
                <div className="flex gap-4">
                  <Button
                    data-testid="btn-captura-manual-desktop"
                    onClick={capturarBiometriaManual}
                    disabled={capturando || capturaProgress.atual >= 5 || !modelsLoaded}
                    variant="outline"
                    className="flex-1 h-14 border-slate-600 text-slate-300 text-lg"
                  >
                    <Camera className="w-6 h-6 mr-2" />
                    Captura Manual
                  </Button>
                  <Button
                    data-testid="btn-fechar-modal-desktop"
                    onClick={closeBiometriaModal}
                    variant="outline"
                    className="h-14 border-slate-600 text-slate-300 px-8 text-lg"
                  >
                    Fechar
                  </Button>
                </div>

                <p className="text-center text-base text-slate-500 mt-4">
                  {modelsLoaded 
                    ? 'Posicione o rosto de frente para a câmera em boa iluminação' 
                    : 'Carregando sistema de biometria...'}
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== TELA DE CONFIGURAÇÃO OFFLINE ====================
const ConfigOffline = ({ config, onVoltar, onLogout }) => {
  const [statusOffline, setStatusOffline] = useState(null);
  const [setores, setSetores] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [filtroSetor, setFiltroSetor] = useState('');
  const [filtroLocalidade, setFiltroLocalidade] = useState('');
  const [sincronizando, setSincronizando] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      // Carregar status offline
      const status = await getStatusOffline(config.empresaId);
      setStatusOffline(status);
      setFiltroSetor(status?.filtro_setor || '');
      setFiltroLocalidade(status?.filtro_localidade || '');

      // Carregar setores e localidades
      const res = await fetch(`${API_URL}/api/empresas-ponto/setores/${config.empresaId}`, {
        headers: { 'Authorization': `Bearer ${config.token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSetores(data.setores || []);
        setLocalidades(data.localidades || []);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSincronizar = async () => {
    setSincronizando(true);
    try {
      const filtro = {};
      if (filtroSetor) filtro.setor = filtroSetor;
      if (filtroLocalidade) filtro.localidade = filtroLocalidade;

      const resultado = await sincronizacaoCompleta(config.empresaId, config.token, filtro);
      
      if (resultado.download.success) {
        toast.success(`Sincronização concluída! ${resultado.download.quantidade} biometrias baixadas.`);
        
        if (resultado.upload.enviados > 0) {
          toast.info(`${resultado.upload.enviados} pontos pendentes sincronizados.`);
        }
      } else {
        toast.error('Erro na sincronização: ' + (resultado.download.error || 'Erro desconhecido'));
      }

      // Atualizar status
      const novoStatus = await getStatusOffline(config.empresaId);
      setStatusOffline(novoStatus);
    } catch (err) {
      toast.error('Erro ao sincronizar');
    } finally {
      setSincronizando(false);
    }
  };

  const handleEnviarPendentes = async () => {
    setSincronizando(true);
    try {
      const resultado = await enviarPontosPendentes(config.empresaId, config.token);
      
      if (resultado.success) {
        toast.success(`${resultado.enviados} pontos sincronizados com sucesso!`);
        const novoStatus = await getStatusOffline(config.empresaId);
        setStatusOffline(novoStatus);
      } else {
        toast.error('Erro ao enviar pontos: ' + resultado.error);
      }
    } catch (err) {
      toast.error('Erro ao enviar pontos pendentes');
    } finally {
      setSincronizando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onVoltar} className="text-slate-400 hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Configurações Offline</h1>
              <p className="text-sm text-slate-400">{config.empresaNome}</p>
            </div>
          </div>
          <Button variant="outline" onClick={onLogout} className="border-slate-600 text-slate-300">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Status Card */}
            <Card className="bg-slate-800/90 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Status do Armazenamento Local
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-sm text-slate-400">Biometrias Offline</p>
                    <p className="text-2xl font-bold text-emerald-400">{statusOffline?.biometrias_offline || 0}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-sm text-slate-400">Pontos Pendentes</p>
                    <p className={`text-2xl font-bold ${statusOffline?.pontos_pendentes > 0 ? 'text-amber-400' : 'text-slate-400'}`}>
                      {statusOffline?.pontos_pendentes || 0}
                    </p>
                  </div>
                </div>
                
                {statusOffline?.ultima_sincronizacao && (
                  <p className="text-sm text-slate-400">
                    Última sincronização: {new Date(statusOffline.ultima_sincronizacao).toLocaleString('pt-BR')}
                  </p>
                )}

                {statusOffline?.dispositivo_id && (
                  <p className="text-sm text-slate-500">
                    ID do Dispositivo: {statusOffline.dispositivo_id}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Filtros */}
            <Card className="bg-slate-800/90 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtros de Sincronização
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Filtre por setor/localidade para reduzir o tamanho da base offline
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Setor</label>
                    <select
                      value={filtroSetor}
                      onChange={(e) => setFiltroSetor(e.target.value)}
                      className="w-full h-10 bg-slate-700 border-slate-600 text-white rounded-lg px-3"
                    >
                      <option value="">Todos os setores</option>
                      {setores.map(setor => (
                        <option key={setor} value={setor}>{setor}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Localidade</label>
                    <select
                      value={filtroLocalidade}
                      onChange={(e) => setFiltroLocalidade(e.target.value)}
                      className="w-full h-10 bg-slate-700 border-slate-600 text-white rounded-lg px-3"
                    >
                      <option value="">Todas as localidades</option>
                      {localidades.map(loc => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-4">
              <Button
                onClick={handleSincronizar}
                disabled={sincronizando}
                className="w-full h-14 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-lg"
              >
                {sincronizando ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-5 h-5 mr-2" />
                )}
                Sincronizar Biometrias
              </Button>

              {statusOffline?.pontos_pendentes > 0 && (
                <Button
                  onClick={handleEnviarPendentes}
                  disabled={sincronizando}
                  className="w-full h-12 bg-amber-600 hover:bg-amber-700"
                >
                  <Cloud className="w-5 h-5 mr-2" />
                  Enviar {statusOffline.pontos_pendentes} Pontos Pendentes
                </Button>
              )}
            </div>

            <p className="text-center text-sm text-slate-500">
              A sincronização baixa as biometrias para o dispositivo permitindo o funcionamento sem internet.
              Quando a conexão for restaurada, os pontos registrados offline serão enviados automaticamente.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== TELA DE EXPORTAÇÃO AFD ====================
const TelaExportarAFD = ({ config, onVoltar }) => {
  const [exportando, setExportando] = useState(false);

  const handleExportar = async (destino) => {
    setExportando(true);
    try {
      const res = await fetch(`${API_URL}/api/empresas-ponto/exportar-afd/${config.empresaId}`);
      
      if (!res.ok) throw new Error('Erro ao gerar AFD');
      
      const data = await res.json();

      // Log de exportação
      fetch(`${API_URL}/api/empresas-ponto/log-exportacao`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          empresa_id: config.empresaId,
          destino: destino,
          total_registros: data.total_registros
        })
      });

      if (destino === 'download') {
        const blob = new Blob([data.conteudo_afd], { type: 'text/plain;charset=iso-8859-1' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.nome_arquivo;
        a.click();
        toast.success('Arquivo AFD baixado!');
      } else if (destino === 'whatsapp') {
        const texto = encodeURIComponent(`Arquivo AFD - ${config.empresaNome}\nPeríodo: ${data.periodo}\nTotal de registros: ${data.total_registros}`);
        window.open(`https://wa.me/?text=${texto}`, '_blank');
      }
    } catch (err) {
      toast.error('Erro ao exportar AFD');
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-slate-800/90 border-slate-700">
        <CardHeader className="text-center border-b border-slate-700">
          <Button
            variant="ghost"
            onClick={onVoltar}
            className="absolute left-4 top-4 text-slate-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-xl font-bold text-white">Exportar AFD</CardTitle>
          <CardDescription className="text-slate-400">
            Arquivo para fiscalização - Portaria MTP 671/2021
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <Button
            onClick={() => handleExportar('download')}
            disabled={exportando}
            className="w-full h-14 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            {exportando ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Download className="w-5 h-5 mr-2" />}
            Baixar Arquivo AFD
          </Button>
          
          <Button
            onClick={() => handleExportar('whatsapp')}
            disabled={exportando}
            variant="outline"
            className="w-full h-12 border-slate-600 text-slate-300"
          >
            <Send className="w-5 h-5 mr-2" />
            Enviar via WhatsApp
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================
const EmpresaPontoApp = () => {
  const [tela, setTela] = useState('selecao');
  const [config, setConfig] = useState(null);
  const [statusOffline, setStatusOffline] = useState(null);
  const [validandoToken, setValidandoToken] = useState(false);

  useEffect(() => {
    // Verificar se há nova versão ao carregar
    const needsReload = checkForUpdates();
    if (needsReload) return; // Se precisa recarregar, não continua

    // Verificar se há token na URL (link de instalação)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenUrl = urlParams.get('token');
    
    if (tokenUrl) {
      validarTokenEConfigurar(tokenUrl);
      return;
    }

    const savedConfig = localStorage.getItem('jornyx_totem_empresa');
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        setConfig(parsed);
        setTela('menu');
        carregarStatusOffline(parsed.empresaId);
      } catch (e) {
        localStorage.removeItem('jornyx_totem_empresa');
      }
    }
    
    // Inicializar IndexedDB
    initDB();
    
    // Alterar o manifest para o manifest específico da empresa
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/manifest-empresa.json';
    }
  }, []);

  const validarTokenEConfigurar = async (token) => {
    setValidandoToken(true);
    try {
      const res = await fetch(`${API_URL}/api/empresa/validar-token-totem?token=${encodeURIComponent(token)}`);
      
      if (res.ok) {
        const data = await res.json();
        
        // Criar configuração do totem
        const novaConfig = {
          empresaId: data.empresa_id,
          empresaNome: data.nome_empresa,
          token: token,
          modoRep: true,
          dispositivoId: localStorage.getItem('jornyx_dispositivo_id') || `totem_${Date.now()}`
        };
        
        // Salvar configuração
        localStorage.setItem('jornyx_totem_empresa', JSON.stringify(novaConfig));
        localStorage.setItem('jornyx_dispositivo_id', novaConfig.dispositivoId);
        
        // Limpar token da URL (para não ficar exposto)
        window.history.replaceState({}, document.title, window.location.pathname);
        
        setConfig(novaConfig);
        setTela('menu');
        carregarStatusOffline(data.empresa_id);
        
        toast.success(`Totem configurado para ${data.nome_empresa}!`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Token inválido ou expirado');
        // Limpar token da URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (err) {
      console.error('Erro ao validar token:', err);
      toast.error('Erro ao validar token. Verifique sua conexão.');
    } finally {
      setValidandoToken(false);
    }
  };

  const carregarStatusOffline = async (empresaId) => {
    try {
      const status = await getStatusOffline(empresaId);
      
      // Também buscar quantidade de biometrias do servidor
      try {
        const res = await fetch(`${API_URL}/api/empresas-ponto/status-biometrias/${empresaId}`);
        if (res.ok) {
          const data = await res.json();
          status.biometrias_servidor = data.total || 0;
        }
      } catch (e) {
        console.log('Não foi possível buscar status do servidor');
      }
      
      setStatusOffline(status);
    } catch (err) {
      console.error('Erro ao carregar status offline:', err);
    }
  };

  const handleEmpresaSelecionada = (novaConfig) => {
    setConfig(novaConfig);
    setTela('menu');
    carregarStatusOffline(novaConfig.empresaId);
  };

  const handleNavigate = (destino) => {
    setTela(destino);
  };

  const handleLoginAdminSuccess = (adminConfig) => {
    setConfig(adminConfig);
    setTela('cadastro');
  };

  const handleLoginConfigSuccess = (adminConfig) => {
    setConfig(adminConfig);
    setTela('config-offline-panel');
  };

  const handleLogoutAdmin = () => {
    const baseConfig = {
      empresaId: config.empresaId,
      empresaNome: config.empresaNome,
      modoRep: config.modoRep,
      dispositivoId: config.dispositivoId
    };
    setConfig(baseConfig);
    localStorage.setItem('jornyx_totem_empresa', JSON.stringify(baseConfig));
    setTela('menu');
  };

  const handleVoltar = () => {
    if (tela === 'menu') {
      localStorage.removeItem('jornyx_totem_empresa');
      setConfig(null);
      setTela('selecao');
    } else if (['ponto', 'login-admin', 'config-offline', 'exportar'].includes(tela)) {
      setTela('menu');
    } else if (['cadastro', 'config-offline-panel'].includes(tela)) {
      setTela('menu');
    }
  };

  // Tela de loading enquanto valida token
  if (validandoToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Card className="w-full max-w-md bg-slate-800/50 backdrop-blur border-slate-700">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-emerald-400" />
            <h2 className="text-xl font-bold text-white mb-2">Configurando Totem</h2>
            <p className="text-slate-400">Validando credenciais...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Renderização por tela
  if (tela === 'selecao' || !config) {
    return <TelaSelecaoEmpresa onEmpresaSelecionada={handleEmpresaSelecionada} />;
  }

  if (tela === 'menu') {
    return (
      <MenuPrincipal 
        config={config} 
        onNavigate={handleNavigate}
        statusOffline={statusOffline}
      />
    );
  }

  if (tela === 'ponto') {
    return <TelaPonto config={config} onVoltar={handleVoltar} />;
  }

  if (tela === 'login-admin') {
    return (
      <TelaLoginAdmin 
        config={config} 
        onLoginSuccess={handleLoginAdminSuccess}
        onVoltar={handleVoltar}
      />
    );
  }

  if (tela === 'cadastro') {
    return (
      <PainelCadastro 
        config={config}
        onVoltar={handleVoltar}
        onLogout={handleLogoutAdmin}
      />
    );
  }

  if (tela === 'config-offline') {
    return (
      <TelaLoginAdmin 
        config={config} 
        onLoginSuccess={handleLoginConfigSuccess}
        onVoltar={handleVoltar}
      />
    );
  }

  if (tela === 'config-offline-panel') {
    return (
      <ConfigOffline 
        config={config}
        onVoltar={handleVoltar}
        onLogout={handleLogoutAdmin}
      />
    );
  }

  if (tela === 'exportar') {
    return <TelaExportarAFD config={config} onVoltar={handleVoltar} />;
  }

  return null;
};

export default EmpresaPontoApp;
